// @ts-ignore
import {Injectable} from '@angular/core';
// @ts-ignore
import {firstValueFrom} from 'rxjs';
import {SankhyaGatewayService} from './sankhya-gateway.service';

/**
 * Item de permissão especial retornado por ControleAcessoSP.getPermissaoAcesso,
 * ex.: {"sigla":"PERCOM","idAcesso":"GPL_vendas","descricao":"Perc Comissão","permissao":true,...}
 */
export interface PermissaoAcessoItem {
    sigla: string;
    idAcesso: string;
    descricao: string;
    grupo: boolean;
    repassar: boolean;
    permissao: boolean;
    especial: boolean;
    podeAlterar: boolean;
}

/**
 * Serviço global de permissões especiais do Controle de Acesso do Sankhya
 * (ControleAcessoSP.getPermissaoAcesso), por resourceID + sigla.
 *
 * Diferente da permissão de MENU (que só valida se o usuário pode ver a tela),
 * este serviço valida permissões ESPECIAIS dentro da tela (ex.: editar preço,
 * editar comissão, ver todas as vendas), cada uma identificada por uma sigla
 * cadastrada no resource (ex.: resource "GPL_vendas" com siglas EDTPRE/PERCOM/VISVEN).
 *
 * Uso:
 *   const podeEditarPreco = await this.permissaoAcesso.podeEditarPreco();
 *   const podeVerTodasVendas = await this.permissaoAcesso.podeVerTodasVendas();
 *
 * Para novas permissões especiais, basta chamar:
 *   this.permissaoAcesso.temPermissao('GPL_vendas', 'NOVA_SIGLA')
 */
@Injectable({providedIn: 'root'})
export class PermissaoAcessoService {

    // ── Resources conhecidos ────────────────────────────────────────────────
    // Mesmo identificador que o menu.component.ts usa (com o prefixo do módulo
    // "gpl-sankhya."), confirmado funcionando via [Menu][Permissao] no console.
    static readonly RESOURCE_VENDAS = 'gpl-sankhya.GPL_vendas';
    static readonly RESOURCE_PRE_VENDA = 'gpl-sankhya.GPL_PreVenda';
    static readonly RESOURCE_RELATORIO_COMISSAO = 'gpl-sankhya.GPL_RelatorioComissoes';
    static readonly RESOURCE_SEPARACAO_ESTOQUE = 'gpl-sankhya.GPL_SeparacaoEstoque';
    static readonly RESOURCE_PAINEL_SEPARACAO = 'gpl-sankhya.GPL_separacao';
    static readonly RESOURCE_CONFERENCIA_ESTOQUE = 'gpl-sankhya.GPL_ConferenciaEstoque';
    static readonly RESOURCE_PAINEL_CONFERENCIA = 'gpl-sankhya.GPL_PainelConferencia';

    // ── Siglas conhecidas do resource GPL_vendas ───────────────────────────
    static readonly SIGLA_EDITAR_PRECO = 'EDTPRE';           // Vl. Unit. U$ / Vl. Unit. R$ nos itens
    static readonly SIGLA_PERC_COMISSAO = 'PERCOM';          // Campo % Comissão (GPL_COMISSAO)
    static readonly SIGLA_VER_TODAS_VENDAS = 'VISVEN';       // Lista de Pedidos: ver vendas de todos ou só as próprias
    static readonly SIGLA_VER_TODAS_COMISSOES_VENDEDORES = 'TODVEN'; // Relatório de Comissões: ver todos vendedores ou só as próprias
    static readonly SIGLA_EDITAR_COTACAO = 'EDTCOT';         // Tela Cotações: pode editar preço/margens do produto
    static readonly SIGLA_EDITAR_COTACAO_MAI = 'EDTMAI';     // Tela Cotações: pode editar SOMENTE para cima (não pode diminuir preço)

    // ── Siglas conhecidas do resource GPL_SeparacaoEstoque (app operacional) ──
    static readonly SIGLA_VER_VENDAS_LEGADO_AUTORIZADO = 'VERAERO'; // Tela Separação (app): ver vendas do Aerosys com status Autorizado

    // ── Siglas conhecidas do resource GPL_separacao (Painel TV/gerencial) ──
    // OBS: "EDTAERO" também é usado no resource GPL_PainelLogistica (outro
    // propósito, "Visualizar Opção de Empresas") — sigla igual, mas resource
    // diferente, então são permissões independentes (chave real é resource+sigla).
    static readonly SIGLA_VER_VENDAS_LEGADO_PAINEL = 'EDTAERO'; // Painel de Separação: ver vendas do Aerosys com status Autorizado

    // ── Siglas conhecidas do resource GPL_ConferenciaEstoque (app operacional) ──
    static readonly SIGLA_VER_VENDAS_LEGADO_CONFERENCIA = 'CONAER'; // Tela Conferência (app): ver vendas do Aerosys em conferência

    // ── Siglas conhecidas do resource GPL_PainelConferencia (Painel TV/gerencial) ──
    static readonly SIGLA_VER_VENDAS_LEGADO_PAINEL_CONFERENCIA = 'PAIAER'; // Painel de Conferência: ver vendas do Aerosys em conferência

    /** Cache por (resourceId::tipo::codigo) para evitar repetir a consulta ao Sankhya a cada verificação. */
    private cache = new Map<string, Promise<PermissaoAcessoItem[]>>();

    /** Cache do grupo "real" do usuário (agrupamento) por resourceId, igual ao menu.component.ts. */
    private grupoRealUsuarioCache: string | null = null;

    constructor(private readonly sankhyaGateway: SankhyaGatewayService) {
    }

    // ── Atalhos do resource GPL_PreVenda (mesmas siglas da Venda) ─────────
    podeEditarPrecoPreVenda(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_PRE_VENDA, PermissaoAcessoService.SIGLA_EDITAR_PRECO);
    }
    podeEditarComissaoPreVenda(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_PRE_VENDA, PermissaoAcessoService.SIGLA_PERC_COMISSAO);
    }
    podeEditarCotacaoPreVenda(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_PRE_VENDA, PermissaoAcessoService.SIGLA_EDITAR_COTACAO);
    }
    podeEditarCotacaoSomenteParaCimaPreVenda(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_PRE_VENDA, PermissaoAcessoService.SIGLA_EDITAR_COTACAO_MAI);
    }
    podeVerTodasVendasPreVenda(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_PRE_VENDA, PermissaoAcessoService.SIGLA_VER_TODAS_VENDAS);
    }

    // ── Atalhos específicos do resource GPL_vendas ─────────────────────────

    /** Vl. Unit. U$ / Vl. Unit. R$ editáveis nos itens do Pedido de Venda. */
    podeEditarPreco(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_VENDAS, PermissaoAcessoService.SIGLA_EDITAR_PRECO);
    }

    /** Campo % Comissão (TGFCAB.GPL_COMISSAO) habilitado para edição. */
    podeEditarComissao(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_VENDAS, PermissaoAcessoService.SIGLA_PERC_COMISSAO);
    }

    /** Tela Cotações: pode editar preço/margens livremente (para cima ou para baixo). */
    podeEditarCotacao(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_VENDAS, PermissaoAcessoService.SIGLA_EDITAR_COTACAO);
    }

    /** Tela Cotações: pode editar SOMENTE para cima — não pode diminuir o preço atual. */
    podeEditarCotacaoSomenteParaCima(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_VENDAS, PermissaoAcessoService.SIGLA_EDITAR_COTACAO_MAI);
    }

    /** Vê todas as vendas na Lista de Pedidos de Venda (filtro, PN, data e carregamento inicial); se false, só as próprias. */
    podeVerTodasVendas(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_VENDAS, PermissaoAcessoService.SIGLA_VER_TODAS_VENDAS);
    }

    /** Tela Separação (app operacional): pode ver a lista de vendas do Aerosys (Sistema Legado) com status Autorizado. */
    podeVerVendasLegadoAutorizado(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_SEPARACAO_ESTOQUE, PermissaoAcessoService.SIGLA_VER_VENDAS_LEGADO_AUTORIZADO);
    }

    /** Painel de Separação (TV/gerencial): pode ver a lista de vendas do Aerosys (Sistema Legado) com status Autorizado. */
    podeVerVendasLegadoAutorizadoPainel(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_PAINEL_SEPARACAO, PermissaoAcessoService.SIGLA_VER_VENDAS_LEGADO_PAINEL);
    }

    /** Tela Conferência (app operacional): pode ver a lista de vendas do Aerosys (Sistema Legado) em conferência. */
    podeVerVendasLegadoConferencia(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_CONFERENCIA_ESTOQUE, PermissaoAcessoService.SIGLA_VER_VENDAS_LEGADO_CONFERENCIA);
    }

    /** Painel de Conferência (TV/gerencial): pode ver a lista de vendas do Aerosys (Sistema Legado) em conferência. */
    podeVerVendasLegadoConferenciaPainel(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_PAINEL_CONFERENCIA, PermissaoAcessoService.SIGLA_VER_VENDAS_LEGADO_PAINEL_CONFERENCIA);
    }

    /** Vê todos vendedores no Relatório de Comissões por Vendedor, se false, só as proprias */
    podeVerTodosVendedoresComissoes(): Promise<boolean> {
        return this.temPermissao(PermissaoAcessoService.RESOURCE_RELATORIO_COMISSAO, PermissaoAcessoService.SIGLA_VER_TODAS_COMISSOES_VENDEDORES);
    }

    // ── API genérica (para novas validações futuras) ───────────────────────

    /**
     * Verifica se o usuário logado tem a permissão especial (sigla) habilitada
     * no resource informado. Usuário SUP (id de sessão "0") sempre tem permissão.
     *
     * Igual ao menu.component.ts (usuarioTemPermissaoNoResource): checa a permissão
     * tanto por USUÁRIO quanto por GRUPO e usa OU entre as duas — no Sankhya é comum
     * a permissão especial estar cadastrada no GRUPO em vez de no usuário individual.
     */
    async temPermissao(resourceId: string, sigla: string): Promise<boolean> {
        if (this.usuarioSup()) {
            return true;
        }

        try {
            const permissoesUsuario = await this.buscarPermissoesAlvo(resourceId, 'USUARIO');
            const permissoesGrupo = await this.buscarPermissoesAlvo(resourceId, 'GRUPO');

            const itemUsuario = permissoesUsuario.find((p: any) => (p?.sigla ?? p?.SIGLA) === sigla);
            const itemGrupo = permissoesGrupo.find((p: any) => (p?.sigla ?? p?.SIGLA) === sigla);

            const permissaoUsuario = this.flagPermissao(itemUsuario?.permissao ?? (itemUsuario as any)?.PERMISSAO);
            const permissaoGrupo = this.flagPermissao(itemGrupo?.permissao ?? (itemGrupo as any)?.PERMISSAO);
            const permitido = permissaoUsuario || permissaoGrupo;

            console.debug('[PermissaoAcesso]', {
                resourceId,
                sigla,
                usuario: this.valorSessao('userName'),
                usuarioIdSessao: this.valorSessao('userId'),
                grupoSessao: this.valorSessao('userGroup'),
                permissoesUsuario,
                permissoesGrupo,
                permissaoUsuario,
                permissaoGrupo,
                permitido
            });

            return permitido;
        } catch (error) {
            console.warn('[PermissaoAcesso] Falha ao consultar permissao', {resourceId, sigla, error});
            return false;
        }
    }

    /** Limpa o cache de permissões (ex.: ao trocar de usuário / logout). */
    limparCache(): void {
        this.cache.clear();
        this.grupoRealUsuarioCache = null;
    }

    // ── Internos ────────────────────────────────────────────────────────────

    /** Busca (com cache) a lista de permissões especiais do resource para USUARIO ou GRUPO. */
    private buscarPermissoesAlvo(resourceId: string, tipo: 'USUARIO' | 'GRUPO'): Promise<PermissaoAcessoItem[]> {
        const chaveCache = `${resourceId}::${tipo}`;

        if (!this.cache.has(chaveCache)) {
            const promise = (async (): Promise<PermissaoAcessoItem[]> => {
                const codigo = tipo === 'USUARIO'
                    ? await this.codigoUsuarioPermissao(resourceId)
                    : await this.codigoGrupoPermissao(resourceId);

                if (!codigo) {
                    console.debug('[PermissaoAcesso] Codigo nao resolvido, pulando consulta', {resourceId, tipo});
                    return [];
                }

                const resposta: any = await firstValueFrom(
                    this.sankhyaGateway.listarPermissaoAcessoControleAcesso(resourceId, tipo, codigo)
                );

                return this.extrairLista(resposta);
            })();

            this.cache.set(chaveCache, promise);
        }

        return this.cache.get(chaveCache)!;
    }

    /**
     * Resolve o CODUSU numérico do Sankhya para o usuário logado.
     *
     * A sessão do gateway guarda em "userId" o USERNAME de login (ver auth.service.ts:
     * `usuarioId: username`), não o código numérico do usuário no Sankhya. O serviço
     * ControleAcessoSP.getPermissaoAcesso exige o CODUSU real em `usuarios.usuario.value`,
     * então replicamos aqui a mesma lógica de menu.component.ts (`codigoUsuarioPermissao`):
     * se o valor da sessão já for numérico usamos direto, senão buscamos a lista de
     * entidades de acesso do resource (getEntidadesAcesso) e casamos pelo nome do usuário.
     */
    private async codigoUsuarioPermissao(resourceId: string): Promise<string> {
        const usuarioId = String(this.valorSessao('userId') || '').trim();

        if (/^\d+$/.test(usuarioId)) {
            return usuarioId;
        }

        const usuarioNome = this.normalizar(this.valorSessao('userName'));
        if (!usuarioNome) {
            return '';
        }

        const usuarios = await this.buscarEntidadesAcesso(resourceId, 'USUARIO');
        const usuario = usuarios.find((item: any) => {
            const descricao = this.normalizar(item?.descricao ?? item?.DESCRICAO ?? item?.nome ?? item?.NOME);
            return descricao === usuarioNome;
        });

        const id = (usuario as any)?.id ?? (usuario as any)?.ID;
        const resultado = id ? String(id) : '';

        console.debug('[PermissaoAcesso][codigoUsuarioPermissao]', {resourceId, usuarioNome, usuariosEncontrados: usuarios, resultado});
        return resultado;
    }

    /** Resolve o CODGRU (ou equivalente) do grupo real do usuário para o resource, igual a menu.component.ts. */
    private async codigoGrupoPermissao(resourceId: string): Promise<string> {
        const grupoNome = this.normalizar(await this.grupoRealUsuario(resourceId));
        if (!grupoNome) {
            return '';
        }

        const grupos = await this.buscarEntidadesAcesso(resourceId, 'GRUPO');
        const grupo = grupos.find((item: any) => {
            const descricao = this.normalizar(item?.descricao ?? item?.DESCRICAO ?? item?.nome ?? item?.NOME);
            return descricao === grupoNome;
        });

        const id = (grupo as any)?.id ?? (grupo as any)?.ID;
        const resultado = id ? String(id) : '';

        console.debug('[PermissaoAcesso][codigoGrupoPermissao]', {resourceId, grupoNome, gruposEncontrados: grupos, resultado});
        return resultado;
    }

    /** Descobre o grupo "real" do usuário (agrupamento cadastrado), com fallback para o grupo da sessão. */
    private async grupoRealUsuario(resourceId: string): Promise<string> {
        if (this.grupoRealUsuarioCache !== null) {
            return this.grupoRealUsuarioCache;
        }

        const usuarioNome = this.normalizar(this.valorSessao('userName'));
        const usuarioId = this.normalizar(this.valorSessao('userId'));

        if (usuarioNome) {
            const usuarios = await this.buscarEntidadesAcesso(resourceId, 'USUARIO', true);
            const usuario = usuarios.find((item: any) => {
                const descricao = this.normalizar(item?.descricao ?? item?.DESCRICAO ?? item?.nome ?? item?.NOME);
                const id = this.normalizar(item?.id ?? item?.ID);
                return descricao === usuarioNome || id === usuarioId;
            });

            const agrupamento = this.normalizar((usuario as any)?.agrupamento ?? (usuario as any)?.AGRUPAMENTO);
            if (agrupamento) {
                this.grupoRealUsuarioCache = agrupamento;
                return agrupamento;
            }
        }

        const grupoSessao = this.normalizar(this.valorSessao('userGroup'));
        this.grupoRealUsuarioCache = this.grupoSessaoEhValido(grupoSessao) ? grupoSessao : '';
        return this.grupoRealUsuarioCache;
    }

    private grupoSessaoEhValido(grupo: string): boolean {
        return !!grupo && grupo !== 'LOCAL' && grupo !== 'LOC' && grupo !== 'SANKHYA-OM';
    }

    /** Busca a lista de entidades de acesso (usuários ou grupos) cadastradas no resource. */
    private async buscarEntidadesAcesso(resourceId: string, tipo: 'USUARIO' | 'GRUPO', semAcesso = false): Promise<any[]> {
        try {
            const resposta: any = await firstValueFrom(
                this.sankhyaGateway.listarEntidadesAcessoControleAcesso([resourceId], tipo, semAcesso)
            );
            return this.extrairLista(resposta);
        } catch (error) {
            console.warn('[PermissaoAcesso] Falha ao buscar entidades de acesso', {resourceId, tipo, error});
            return [];
        }
    }

    private normalizar(valor: unknown): string {
        return String(valor ?? '').trim().toUpperCase();
    }

    private extrairLista(resposta: any): PermissaoAcessoItem[] {
        if (!resposta) return [];
        if (Array.isArray(resposta)) return resposta;

        const body = resposta.responseBody ?? resposta.serviceResponse?.responseBody ?? resposta;
        if (Array.isArray(body)) return body;
        if (Array.isArray(body?.json)) return body.json;
        if (typeof body?.json === 'string') return this.parseListaJson(body.json);
        if (typeof body?.json?.$ === 'string') return this.parseListaJson(body.json.$);
        if (typeof body?.json?._ === 'string') return this.parseListaJson(body.json._);
        if (body?.responseBody) return this.extrairLista(body.responseBody);
        if (typeof body === 'string') return this.parseListaJson(body);
        return [];
    }

    private parseListaJson(valor: string): PermissaoAcessoItem[] {
        try {
            const cdata = valor.match(/<!\[CDATA\[([\s\S]*?)\]\]>/);
            const parsed = JSON.parse(cdata?.[1] || valor);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private flagPermissao(valor: unknown): boolean {
        return valor === true ||
            valor === 'true' ||
            valor === 'TRUE' ||
            valor === 'S' ||
            valor === '1' ||
            valor === 1;
    }

    private usuarioSup(): boolean {
        const usuarioId = String(this.valorSessao('userId') || '').trim();
        const usuarioNome = String(this.valorSessao('userName') || '').trim().toUpperCase();
        return usuarioId === '0' || usuarioNome === 'SUP';
    }

    private valorSessao(chave: string): string {
        const tipo = localStorage.getItem('authStorageType');
        if (tipo === 'session') {
            return sessionStorage.getItem(chave) || '';
        }
        return localStorage.getItem(chave) || sessionStorage.getItem(chave) || '';
    }
}
